/**
 * React Query key factory — EPIC 43_CHK (checklists de suivi).
 *
 * ⚠️ `entityId` entre dans CHAQUE clé, et ce n'est pas décoratif : la banque des
 * modèles dépend du VIEWER (entité courante ∪ ses ascendants, CHK-003 §4.4).
 * Sans lui, changer d'entité rendrait le cache de la précédente — avec ses
 * modèles hérités attribués à la mauvaise maison mère.
 *
 * L'étoile est scopée par (typologie, entité) : une typologie a son défaut, et
 * déplacer celui des clients ne doit pas invalider celui des formations.
 */

const checklistsAll = ['checklists'] as const

export const checklistsKeys = {
    checklists: {
        all: checklistsAll,
        /** Banque des modèles, filtres serveur compris (recherche, typologie, actifs). */
        templates: (
            entityId: string | null,
            filters?: { q?: string; typology?: string | null; onlyActive?: boolean },
        ) => [...checklistsAll, 'templates', entityId, filters ?? {}] as const,
        /** Racine des modèles — pour invalider toute la banche d'une entité. */
        templatesRoot: (entityId: string | null) =>
            [...checklistsAll, 'templates', entityId] as const,
        /** Un modèle et son arbre (CHK-005). */
        template: (entityId: string | null, templateId: string) =>
            [...checklistsAll, 'template', entityId, templateId] as const,
        /** L'étoile d'une typologie pour une entité (CHK-010). */
        defaults: (entityId: string | null, typology: string) =>
            [...checklistsAll, 'defaults', entityId, typology] as const,
        /** Les suivis rattachés à une fiche — c'est aussi LA SÉRIE des
         *  vérifications datées, en régime « à chaque fois ».
         *  ⚠️ **PRÉFIXE d'invalidation**, plus une clé de requête depuis que la
         *  série se pagine : c'est `instancesPaged` qu'on interroge. Les deux
         *  gestes restent justes — invalider ce préfixe emporte toutes les
         *  tailles de page ET l'archive, qui bougent bien ensemble. */
        instances: (entityId: string | null, hostType: string, hostId: string) =>
            [...checklistsAll, 'instances', entityId, hostType, hostId] as const,
        /** TOUTES les séries d'une entité — pour qu'une écriture qui change un
         *  rollup rafraîchisse la ligne d'où l'écran tire sa jauge.
         *
         *  🔴 **Elle est née d'un correctif qui, sans elle, en aurait cassé un
         *  autre.** Depuis que la carte REPLIÉE lit sa jauge sur `pilotage`
         *  (la ligne de la série) au lieu de `completion-summary`, une coche
         *  laisse la ligne périmée : `useResolveChecklistPoint` n'invalidait que
         *  la vue et le résumé. On aurait donc échangé N requêtes par fiche
         *  contre une jauge FAUSSE sur une pièce opposable. L'appelant ne
         *  connaît que l'entité (`useResolveChecklistPoint(entityId,
         *  instanceId)`), d'où la racine — même forme et même motif que
         *  `templatesRoot`.
         *
         *  ⚠️ Elle emporte les séries de TOUTES les fiches de l'entité, mais
         *  `invalidateQueries` ne REFETCHE que les requêtes actives : sur un
         *  écran de fiche il y en a exactement une — **plus l'archive, si
         *  l'accordéon est ouvert** (`instancesArchived` étend ce préfixe).
         *
         *  🔴 **Le COÛT annoncé ici était faux, et il l'était d'un facteur qui
         *  grandit.** Il disait « +1 refetch de série (4 SQL) par coche », en
         *  comptant les REQUÊTES et non les PAGES : la série est un
         *  `useInfiniteQuery`, et un `useInfiniteQuery` invalidé refait **toutes
         *  ses pages chargées**, séquentiellement (query-core 5.90.20, branche
         *  sans `direction` : `while (currentPage < remainingPages)`). Le vrai
         *  coût est donc **4 SQL × (pages chargées)**, et c'est l'anti-patron
         *  mesuré cent lignes plus bas (`sessionOccurrences`, « douze appels
         *  parasites par case cochée »).
         *
         *  ⚠️ Ce qui l'a ramené à sa forme nominale n'est pas un `maxPages` — il
         *  JETTE des pages, donc des constats, en silence — mais le retrait du
         *  `pageSize` de 5 : une fiche charge sa page serveur (50) d'un coup, et
         *  n'en accumule une seconde qu'au-delà de 50 constats. La pagination
         *  des 5 est devenue visuelle (`useSerieAffichee`) et ne coûte rien. */
        instancesRoot: (entityId: string | null) =>
            [...checklistsAll, 'instances', entityId] as const,
        /** LA SÉRIE PAGINÉE d'une fiche — la clé réellement interrogée.
         *
         *  ⚠️ **Distincte de `instances`, et SŒUR de `instancesArchived`.** Les
         *  deux étendent le même préfixe : invalider la fiche les emporte
         *  ensemble (archiver déplace un suivi de l'une vers l'autre), et
         *  chacune reste visable séparément.
         *
         *  🔴 **Elle a porté un `pageSize`, retiré le 2026-09-08.** Il séparait
         *  le cache de l'onglet (5) de celui de la tuile de synthèse (défaut
         *  serveur) — deux requêtes pour une même série sur la fiche d'un
         *  matériel. La taille de page ayant quitté le réseau, les deux lecteurs
         *  repartagent une seule requête, comme avant le lot. */
        instancesPaged: (
            entityId: string | null, hostType: string, hostId: string,
        ) => [...checklistsAll, 'instances', entityId, hostType, hostId,
            'page'] as const,
        /** La ZONE D'ARCHIVE d'une fiche — les suivis qu'on en a retirés.
         *  ⚠️ Clé PRÉFIXÉE par `instances` : invalider la fiche invalide donc
         *  aussi son archive, ce qui est exactement ce qu'on veut — archiver
         *  déplace un suivi de l'une vers l'autre, les deux changent ensemble. */
        instancesArchived: (entityId: string | null, hostType: string, hostId: string) =>
            [...checklistsAll, 'instances', entityId, hostType, hostId, 'archived'] as const,
        /** Le badge « N tâches liées » d'une fiche (CHK-009 §6.2).
         *
         *  🔴 **Racine PROPRE (`task-count`), et elle a dû le devenir.** La clé
         *  était composée DANS le hook — `[...instances(…), 'tasks']` — donc
         *  hors de cette fabrique, donc invisible au gardien qui la parcourt.
         *  C'était un sur-ensemble de `instances` : rattacher, détacher,
         *  archiver ou jouer un geste (`close`, `reopen`, `restart`,
         *  `hard-refresh`) emportait le badge avec lui, et son `staleTime` de
         *  30 s ne servait plus à rien. Or le compte se lit dans
         *  `notifications` (`checklists_repository/tasks.py`) : AUCUNE écriture
         *  sur un suivi ne peut le changer. Le refetch était intégralement
         *  perdu, scope d'entités descendantes résolu à chaque fois.
         *
         *  ⚠️ Ce qui le change VRAIMENT, c'est créer une tâche depuis un point —
         *  et cela ne passait par aucune invalidation : le badge se rafraîchit
         *  désormais sur le `onCreated` de la modale (ChecklistInstanceCard). */
        taskCount: (entityId: string | null, hostType: string, hostId: string) =>
            [...checklistsAll, 'task-count', entityId, hostType, hostId] as const,
        /** Un suivi rendu : structure résolue + complétions. */
        instanceView: (entityId: string | null, instanceId: string) =>
            [...checklistsAll, 'instance', entityId, instanceId] as const,
        /** Le JOURNAL d'un suivi — pagination keyset (`useInfiniteQuery`).
         *  ⚠️ **Clé PRÉFIXÉE par `instanceView`, et c'est DÉCIDÉ** : une coche
         *  ajoute une ligne au journal, donc invalider la vue doit l'emporter.
         *  🔴 **La décision tient à une CONDITION, écrite ici pour qu'on la
         *  relise le jour où elle tombe** : le journal n'est monté que dans une
         *  modale, et sa requête est désactivée dialogue fermé
         *  (`ChecklistHistoryDialog` passe `open ? instanceId : null`). Aucune
         *  coche ne peut donc partir pendant qu'il est ouvert. Rendre
         *  l'historique consultable HORS modale change ce calcul : invalider un
         *  `useInfiniteQuery` refait TOUTES ses pages chargées, à chaque coche.
         *  Il faudra alors rompre le préfixage — pas le découvrir. */
        auditLog: (entityId: string | null, instanceId: string) =>
            [...checklistsAll, 'instance', entityId, instanceId, 'audit'] as const,
        /** Le modèle de session désigné par une formation ou un client
         *  (CHK-010 §7.6).
         *
         *  🔴 **Racine PROPRE (`designation`), et elle a dû le devenir.** La clé
         *  était composée DANS le hook — `[...checklistsAll, 'designation', …]` —
         *  donc hors de cette fabrique, donc invisible au gardien qui la
         *  parcourt. Elle n'étendait « que » la racine `all`, ce qu'on peut
         *  croire inerte tant que personne n'invalide `all` seul : c'est un fait
         *  MESURÉ un jour, que rien ne garde. Le jour où quelqu'un écrit
         *  `invalidateQueries({ queryKey: queryKeys.checklists.all })`, toute la
         *  banque, tous les journaux et toutes les séries repartent — et une clé
         *  que la fabrique ne déclare pas n'a personne pour le voir. */
        designation: (entityId: string | null, scopeType: string, scopeRef: string) =>
            [...checklistsAll, 'designation', entityId, scopeType, scopeRef] as const,
        /** Les déclencheurs automatiques d'un point (CHK-013).
         *  Clé PROPRE : ils ne changent qu'à leur propre édition, et rien
         *  d'autre ne doit les emporter. */
        triggers: (entityId: string | null, itemId: string | null) =>
            [...checklistsAll, 'triggers', entityId, itemId] as const,
        /** Les compteurs de la jauge — séparés de la vue : ils bougent au même
         *  moment, mais l'un est agrégé serveur et l'autre pas. */
        summary: (entityId: string | null, instanceId: string) =>
            [...checklistsAll, 'summary', entityId, instanceId] as const,
        /** La méga-checklist d'une session (CHK-011 §6 → écran CHK-012).
         *  ⚠️ Clé DISTINCTE de `instances` : ce n'est pas la liste des suivis
         *  d'une fiche, c'est l'agrégat multi-source d'une session — roster
         *  résolu, agrégats par point, série d'occurrences embarquée. Les
         *  confondre ferait qu'un ajout de source invaliderait le mauvais
         *  cache, et l'écran garderait l'état d'avant. */
        sessionChecklist: (entityId: string | null, sessionId: string) =>
            [...checklistsAll, 'session', entityId, sessionId] as const,
        /** La SÉRIE paginée des constats d'UNE source (CHK-011 §7.8).
         *  ⚠️ Clé portée par l'`instance_id`, jamais par le modèle : deux
         *  clients d'une même session peuvent désigner le même modèle, et
         *  mêler leurs séries mêlerait les preuves de deux donneurs d'ordre.
         *
         *  🔴 **Racine PROPRE (`session-occurrences`), pas un préfixe étendu de
         *  `sessionChecklist`.** Elle valait
         *  `[…, 'session', entityId, sessionId, 'occurrences', instanceId]` —
         *  donc un SUR-ENSEMBLE de la clé de la méga-checklist. Or
         *  `invalidateQueries` travaille par PRÉFIXE : les trois invalidations
         *  de `sessionChecklist` — dont celle qui part à CHAQUE coche —
         *  emportaient aussi toutes les séries dépliées. Un `useInfiniteQuery`
         *  invalidé refetche TOUTES ses pages : quatre sources dépliées à trois
         *  pages faisaient douze appels parasites par case cochée, chacun
         *  re-résolvant le roster complet côté serveur. Et c'est du travail
         *  intégralement perdu : les constats précédents sont CLOS, donc en
         *  lecture seule — leurs compteurs ne peuvent pas avoir changé.
         *
         *  ⚠️ Le préfixage volontaire existe juste au-dessus (`instancesArchived`)
         *  et il y est COMMENTÉ comme tel : archiver déplace un suivi d'une
         *  liste vers l'autre, les deux changent ensemble. Ici, rien de pareil
         *  n'était voulu ni écrit — c'était une conséquence, pas une décision. */
        sessionOccurrences: (
            entityId: string | null, sessionId: string, instanceId: string,
        ) => [...checklistsAll, 'session-occurrences', entityId, sessionId, instanceId] as const,
    },
}
